/* eslint-disable no-unused-vars */

/* eslint-disable react/prop-types */

/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { Form } from 'react-bootstrap';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';

import Icon from 'components/shared/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledGroup = styled(
  forwardRef(({ bottomSpace, vertical, ...props }, ref) => <Form.Group ref={ref} {...props} />)
)`
  display: flex;
  flex-wrap: wrap;

  ${({ vertical }) =>
    vertical &&
    css`
      flex-direction: column;
    `}

  ${({ bottomSpace }) =>
    bottomSpace &&
    css`
      margin-bottom: ${bottomSpace}px;
    `}

  input:checked,
  input:not(:checked) {
    position: absolute;
    left: -9999px;
  }
`;

const StyledCheck = styled(
  forwardRef(({ bottomSpace, visible, ...props }, ref) => <Form.Check ref={ref} {...props} />)
)`
  margin-right: ${spaces.largeX + 2}px;

  .form-check-input.is-valid ~ .form-check-label {
    color: ${colors.green.primary};
  }

  .form-check-input.is-invalid ~ .form-check-label {
    color: ${colors.red.primary};
  }

  ${({ visible }) =>
    !visible &&
    css`
      display: none;
    `}

  ${({ bottomSpace, visible }) =>
    bottomSpace &&
    visible &&
    css`
      label {
        margin-bottom: ${spaces.regular}px;
      }
    `}
`;

const StyledCheckInput = styled(Form.Check.Input)``;

const StyledCheckLabel = styled(forwardRef(({ checked, ...props }, ref) => <Form.Check.Label ref={ref} {...props} />))`
  display: flex;

  font-family: 'RethinkSans';
  font-size: ${fontSizes.regular}px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  color: ${colors.gray.secondary};

  cursor: pointer;

  ${({ checked }) =>
    checked &&
    css`
      color: ${colors.gray.primary};
    `}

  @media ${devices.tablet} {
    align-items: flex-start;
  }
`;

const StyledFeedback = styled(
  forwardRef(({ visible, ...props }, ref) => <Form.Control.Feedback ref={ref} {...props} />)
)`
  width: 100%;
  padding-top: ${theme.spaces.small2X}px;
  padding-left: ${spaces.smallest}px;

  font-family: 'RethinkSans';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  color: ${colors.gray.secondary};

  &.processing-feedback {
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    color: ${colors.gray.secondary};
  }

  &.valid-feedback {
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    color: ${colors.green.primary};
  }

  &.invalid-feedback {
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    color: ${colors.red.primary};
  }
`;

const StyledIconContainer = styled.span`
  margin-right: ${spaces.smallX}px;

  svg {
    margin-top: ${-spaces.small2X}px;
  }
`;

function RadioGroup({
  name,
  disabled,
  bottomSpace,
  showActiveOnly,
  vertical,
  valid,
  invalid,
  feedback,
  options = [],
  value,
  onChange,
  ...restProps
}) {
  return (
    <StyledGroup vertical={vertical} bottomSpace={bottomSpace} className={invalid ? 'custom-control-invalid' : ''}>
      {options.map((option, idx) => (
        <StyledCheck
          key={`${name}${idx + 1}`}
          id={`${name}${idx + 1}`}
          bottomSpace={vertical && !showActiveOnly && idx !== options.length - 1}
          visible={showActiveOnly ? value === option.value : true}
        >
          <StyledCheckInput
            {...restProps}
            type="radio"
            disabled={disabled}
            isInvalid={invalid}
            checked={value === option.value}
            value={option.value}
            onChange={onChange}
          />
          <StyledCheckLabel checked={value === option.value}>
            <StyledIconContainer>
              {value === option.value && (
                <Icon alias={ICON_ALIASES.radioOn} color={colors.cyanBlue} width={16} height={16} />
              )}
              {value !== option.value && (
                <Icon alias={ICON_ALIASES.radioOff} color={colors.gray.secondary} width={16} height={16} />
              )}
            </StyledIconContainer>
            <span>{option.label}</span>
          </StyledCheckLabel>
        </StyledCheck>
      ))}
      {feedback && (
        <StyledFeedback type="processing" visible={!valid && !invalid && feedback}>
          {feedback}
        </StyledFeedback>
      )}
      {feedback && (
        <StyledFeedback type="valid" visible={valid}>
          {feedback}
        </StyledFeedback>
      )}
      {feedback && (
        <StyledFeedback type="invalid" visible={invalid}>
          {feedback}
        </StyledFeedback>
      )}
    </StyledGroup>
  );
}

RadioGroup.propTypes = {
  name: PropTypes.string,
  disabled: PropTypes.bool,
  bottomSpace: PropTypes.number,
  showActiveOnly: PropTypes.bool,
  vertical: PropTypes.bool,
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  feedback: PropTypes.node,
  options: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.any })),
  value: PropTypes.any,
  onChange: PropTypes.func,
};

export default RadioGroup;
